/**
 * Holdings API (TMMAPP-0018, design §12 / §8.10) — wire types from `src/api/generated/`;
 * normalized into the module's domain type. Every optional wire field is `| null`.
 * `quantity` is a JS number from the wire decimal — display only, never recomputed (MM-D-23).
 */
import { request } from './client';
import { ApiShapeError } from './errors';
import type {
  HoldingCreateRequest as WireCreate,
  HoldingPatchRequest as WirePatch,
  HoldingReorderRequest,
  HoldingResponse as WireHolding,
  HoldingValuationRequest as WireValuationCreate,
  HoldingValuationResponse as WireValuation,
} from './generated';

export type HoldingType = 'FIXED_ASSET' | 'TRADABLE';
export type HoldingSubtype = 'REAL_ESTATE' | 'VEHICLE' | 'STOCK' | 'CRYPTO';
export type HoldingStatus = 'ACTIVE' | 'DISPOSED';
export type PriceSource = 'MANUAL' | 'API';
export type ValuationSource = 'MANUAL' | 'COMPUTED' | 'API';

export const SUBTYPES_BY_HOLDING_TYPE: Record<HoldingType, HoldingSubtype[]> = {
  FIXED_ASSET: ['REAL_ESTATE', 'VEHICLE'],
  TRADABLE: ['STOCK', 'CRYPTO'],
};

export interface Holding {
  id: number;
  name: string;
  type: HoldingType;
  subtype: HoldingSubtype;
  status: HoldingStatus;
  disposedOn: string | null;
  description: string | null;
  acquiredOn: string | null;
  acquisitionCostMinor: number | null;
  valuationMinor: number | null;
  valuationOn: string | null;
  annualRateBp: number | null;
  financedByAccountId: number | null;
  symbol: string | null;
  quantity: number | null;
  costPerUnitMinor: number | null;
  currentPriceMinor: number | null;
  currentPriceAsOf: string | null;
  priceSource: PriceSource | null;
  asOf: string;
  estimatedValueMinor: number | null;
  gainMinor: number | null;
  gainBp: number | null;
  equityMinor: number | null;
  owedMinor: number | null;
  beginningValueMinor: number | null;
  currentValueMinor: number | null;
  /** When the holding was recorded as held; null when the wire omits it. */
  heldAt: string | null;
  /** Server order within the holding type. */
  sortOrder: number;
}

export interface HoldingValuation {
  id: number;
  asOf: string;
  valueMinor: number;
  source: ValuationSource;
  createdAt: string;
}

/** A create body: only the fields the person set. */
export type HoldingCreate = WireCreate;
/**
 * A patch body: only the fields the person changed. `financedByAccountId: null` removes the
 * financing loan; every other field is omitted to leave it unchanged.
 */
export type HoldingPatch = WirePatch;
export type HoldingValuationCreate = WireValuationCreate;

const HOLDING_TYPES: ReadonlySet<string> = new Set(['FIXED_ASSET', 'TRADABLE']);
const HOLDING_SUBTYPES: ReadonlySet<string> = new Set([
  'REAL_ESTATE',
  'VEHICLE',
  'STOCK',
  'CRYPTO',
]);

function subtypeFor(type: HoldingType, raw: string): HoldingSubtype {
  if (HOLDING_SUBTYPES.has(raw)) return raw as HoldingSubtype;
  return SUBTYPES_BY_HOLDING_TYPE[type][0]!;
}

function statusOf(value: string): HoldingStatus {
  return value === 'DISPOSED' ? 'DISPOSED' : 'ACTIVE';
}

function priceSourceOf(value: string | undefined): PriceSource | null {
  if (value === 'MANUAL' || value === 'API') return value;
  return null;
}

function valuationSourceOf(value: string): ValuationSource {
  if (value === 'COMPUTED' || value === 'API') return value;
  return 'MANUAL';
}

/** A holding row; `null` (dropped) with a type this build doesn't know. */
export function normalizeHolding(w: WireHolding): Holding | null {
  if (!HOLDING_TYPES.has(w.type)) {
    if (import.meta.env.DEV) console.warn('holdings: unknown type dropped', w.type);
    return null;
  }
  const type: HoldingType = w.type;
  return {
    id: w.id,
    name: w.name,
    type,
    subtype: subtypeFor(type, w.subtype),
    status: statusOf(w.status),
    disposedOn: w.disposedOn ?? null,
    description: w.description ?? null,
    acquiredOn: w.acquiredOn ?? null,
    acquisitionCostMinor: w.acquisitionCostMinor ?? null,
    valuationMinor: w.valuationMinor ?? null,
    valuationOn: w.valuationOn ?? null,
    annualRateBp: w.annualRateBp ?? null,
    financedByAccountId: w.financedByAccountId ?? null,
    symbol: w.symbol ?? null,
    quantity: w.quantity ?? null,
    costPerUnitMinor: w.costPerUnitMinor ?? null,
    currentPriceMinor: w.currentPriceMinor ?? null,
    currentPriceAsOf: w.currentPriceAsOf ?? null,
    priceSource: priceSourceOf(w.priceSource),
    asOf: w.asOf,
    estimatedValueMinor: w.estimatedValueMinor ?? null,
    gainMinor: w.gainMinor ?? null,
    gainBp: w.gainBp ?? null,
    equityMinor: w.equityMinor ?? null,
    owedMinor: w.owedMinor ?? null,
    beginningValueMinor: w.beginningValueMinor ?? null,
    currentValueMinor: w.currentValueMinor ?? null,
    heldAt: w.heldAt ?? null,
    sortOrder: w.sortOrder,
  };
}

export function normalizeHoldings(value: readonly WireHolding[]): Holding[] {
  return value.flatMap((row) => {
    const holding = normalizeHolding(row);
    return holding ? [holding] : [];
  });
}

function requiredHolding(wire: WireHolding): Holding {
  const holding = normalizeHolding(wire);
  if (!holding) throw new ApiShapeError(`holding.type ${wire.type} is unknown`);
  return holding;
}

export function normalizeValuation(w: WireValuation): HoldingValuation {
  return {
    id: w.id,
    asOf: w.asOf,
    valueMinor: w.valueMinor,
    source: valuationSourceOf(w.source),
    createdAt: w.createdAt,
  };
}

export function normalizeValuations(value: readonly WireValuation[]): HoldingValuation[] {
  return value
    .map(normalizeValuation)
    .sort((a, b) => (a.asOf < b.asOf ? 1 : a.asOf > b.asOf ? -1 : 0));
}

export const holdingsApi = {
  /** GET /v1/me/holdings — ACTIVE and DISPOSED; the page filters. */
  async list(): Promise<Holding[]> {
    return normalizeHoldings(await request<WireHolding[]>('GET', '/v1/me/holdings'));
  },
  /** GET /v1/me/holdings/{id}. */
  async get(id: number): Promise<Holding> {
    return requiredHolding(await request<WireHolding>('GET', `/v1/me/holdings/${id}`));
  },
  /** POST /v1/me/holdings. */
  async create(body: HoldingCreate): Promise<Holding> {
    return requiredHolding(
      await request<WireHolding>('POST', '/v1/me/holdings', {
        json: body,
      }),
    );
  },
  /**
   * PATCH /v1/me/holdings/{id} — patchable fields only (never type / subtype / quantity / prices),
   * and only the ones the person changed. `financedByAccountId: null` removes the loan.
   */
  async patch(id: number, body: HoldingPatch): Promise<Holding> {
    return requiredHolding(
      await request<WireHolding>('PATCH', `/v1/me/holdings/${id}`, {
        json: body,
      }),
    );
  },
  /** DELETE /v1/me/holdings/{id} — disposes; valuations are retained. */
  async dispose(id: number): Promise<void> {
    await request<unknown>('DELETE', `/v1/me/holdings/${id}`);
  },
  /** GET /v1/me/holdings/{id}/valuations — newest first (re-sort by asOf desc). */
  async listValuations(id: number): Promise<HoldingValuation[]> {
    return normalizeValuations(
      await request<WireValuation[]>('GET', `/v1/me/holdings/${id}/valuations`),
    );
  },
  /** POST /v1/me/holdings/reorder — no UI; 0096 adopts it. */
  async reorder(type: HoldingReorderRequest['type'], holdingIds: number[]): Promise<Holding[]> {
    return normalizeHoldings(
      await request<WireHolding[]>('POST', '/v1/me/holdings/reorder', {
        json: { type, holdingIds } satisfies HoldingReorderRequest,
      }),
    );
  },
  /** POST /v1/me/holdings/{id}/valuations. */
  async addValuation(id: number, body: HoldingValuationCreate): Promise<HoldingValuation> {
    return normalizeValuation(
      await request<WireValuation>('POST', `/v1/me/holdings/${id}/valuations`, { json: body }),
    );
  },
};
